import { Box, Divider, Paper, Slide, Typography } from '@mui/material';
import React from 'react';
import { useNavigate } from 'react-router-dom';
import MessageToast from '../MessageToast';
import { ChatAiAssistPanel } from './ChatAiAssistDrawer';
import ChatResolveDraftsDialog from './ChatResolveDraftsDialog';
import FidesChatComposeBar from './FidesChatComposeBar';
import FidesChatContextBar from './FidesChatContextBar';
import FidesChatMessageTimeline from './FidesChatMessageTimeline';
import FidesChatMinimizedPill from './FidesChatMinimizedPill';
import FidesChatPanelHeader from './FidesChatPanelHeader';
import FidesChatTeamSection from './FidesChatTeamSection';
import { FIDES_CHAT_PANEL_LAYOUT } from './fidesChatPanelLayout';
import { useFidesChatPanel } from '../../hooks/admin/useFidesChatPanel';

const FidesChatPanel: React.FC = () => {
  const navigate = useNavigate();
  const panel = useFidesChatPanel();

  if (!panel.chat) {
    return null;
  }

  const chat = panel.chat;
  const team = panel.team;

  return (
    <>
      <ChatResolveDraftsDialog
        open={panel.resolveDraftsOpen}
        conversationId={chat.openConversationId}
        entityOptions={panel.resolveEntityOptions}
        onClose={() => panel.setResolveDraftsOpen(false)}
        onSaved={(counts) => {
          const parts = [];
          if (counts.notes) parts.push(`${counts.notes} note${counts.notes === 1 ? '' : 's'}`);
          if (counts.actions)
            parts.push(`${counts.actions} action${counts.actions === 1 ? '' : 's'}`);
          panel.setToastMessage({
            type: 'success',
            text: parts.length ? `Saved ${parts.join(' and ')}.` : 'Saved.',
          });
        }}
      />

      {panel.toastMessage && (
        <MessageToast
          type={panel.toastMessage.type}
          message={panel.toastMessage.text}
          open={true}
          onClose={() => panel.setToastMessage(null)}
        />
      )}

      {chat.isPanelOpen && chat.isChatMinimized && (
        <FidesChatMinimizedPill
          contactName={panel.contactName}
          onRestore={() => chat.restorePanel()}
          onClose={() => chat.closePanel()}
        />
      )}

      <ChatAiAssistPanel
        assist={panel.chatAiAssist}
        conversationId={chat.openConversationId}
        corpus={panel.chatLinkCorpus}
        chatOpen={Boolean(chat.isPanelOpen && !chat.isChatMinimized)}
        onUseInvestorResponse={panel.setInputValue}
      />

      <Slide
        direction="left"
        in={chat.isPanelOpen && !chat.isChatMinimized}
        mountOnEnter
        unmountOnExit
      >
        <Paper
          elevation={12}
          sx={{
            position: 'fixed',
            top: FIDES_CHAT_PANEL_LAYOUT.top,
            right: 0,
            width: FIDES_CHAT_PANEL_LAYOUT.chatWidth,
            height: FIDES_CHAT_PANEL_LAYOUT.height,
            zIndex: FIDES_CHAT_PANEL_LAYOUT.zIndex,
            boxShadow: '0 12px 28px rgba(0, 0, 0, 0.18)',
            borderTopLeftRadius: 16,
            borderBottomLeftRadius: 16,
            overflow: 'hidden',
            display: 'flex',
            flexDirection: 'column',
            backgroundColor: '#fff',
          }}
        >
          <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', minHeight: 0 }}>
            <FidesChatPanelHeader
              contactName={panel.contactName}
              contactEmail={panel.contactEmail}
              isTranscriptMode={panel.isTranscriptMode}
              hasTeamReply={panel.hasTeamReply}
              openConversationId={chat.openConversationId}
              onOpenDraftNotes={panel.openDraftNotes}
              onMinimize={() => chat.minimizePanel()}
              onClose={chat.closePanel}
            />

            <FidesChatContextBar
              contextData={panel.contextData}
              contextLabel={panel.contextLabel}
              contextLink={panel.contextLink}
              onNavigate={(path) => navigate(path)}
            />

            {!panel.isTranscriptMode && (
              <FidesChatTeamSection
                teamSectionExpanded={team.teamSectionExpanded}
                onToggleTeamSection={() => team.setTeamSectionExpanded((prev) => !prev)}
                participantsLoading={team.participantsLoading}
                isUnowned={team.isUnowned}
                effectiveOwnerName={panel.effectiveOwnerName}
                participants={team.participants}
                isOwner={team.isOwner}
                openConversationId={chat.openConversationId}
                isClaiming={team.isClaiming}
                onClaim={() =>
                  void team.handleClaim(
                    (text) => panel.showToast('success', text),
                    (text) => panel.showToast('error', text),
                  )
                }
                onRemoveParticipant={(id) =>
                  void team.handleRemoveParticipant(
                    id,
                    (text) => panel.showToast('success', text),
                    (text) => panel.showToast('error', text),
                  )
                }
                canInviteAdmins={team.canInviteAdmins}
                isAddingParticipant={team.isAddingParticipant}
                inviteSearchOptions={team.inviteSearchOptions}
                adminSearchLoading={team.adminSearchLoading}
                inviteSearchInput={team.inviteSearchInput}
                onInviteSearchInputChange={team.handleInviteSearchInputChange}
                onInviteAdmin={(admin) =>
                  void team.handleInviteAdmin(
                    admin,
                    (text) => panel.showToast('success', text),
                    (text) => panel.showToast('error', text),
                  )
                }
                adminSearchOptions={team.adminSearchOptions}
                transferOwnerValue={team.transferOwnerValue}
                transferSearchInput={team.transferSearchInput}
                onTransferSearchInputChange={team.handleTransferSearchInputChange}
                onTransferOwnerValueChange={team.setTransferOwnerValue}
                isTransferring={team.isTransferring}
                onTransferOwnership={() =>
                  void team.handleTransferOwnership(
                    (text) => panel.showToast('success', text),
                    (text) => panel.showToast('error', text),
                  )
                }
              />
            )}

            <Divider />

            <FidesChatMessageTimeline
              loading={panel.loading}
              displayTimeline={panel.displayTimeline}
              currentAdminExternalId={team.currentAdminExternalId}
              contactExternalId={panel.contactExternalId}
              convId={panel.convId}
              chatLinkCorpus={panel.chatLinkCorpus}
              chatAiAssist={panel.chatAiAssist}
              onNavigate={(path) => navigate(path)}
              messagesEndRef={panel.messagesEndRef}
            />

            {!panel.isTranscriptMode && (
              <FidesChatComposeBar
                inputValue={panel.inputValue}
                onInputChange={panel.setInputValue}
                onSend={() => void panel.handleSend()}
                isSending={panel.isSending}
                conversationEnded={panel.conversationEnded}
                openConversationId={chat.openConversationId}
                liveResolvedBy={panel.liveResolvedBy}
                chatAiAssist={panel.chatAiAssist}
                hasInvestorMessage={panel.hasInvestorMessage}
              />
            )}

            {panel.isTranscriptMode && (
              <Box
                sx={{ px: 2, py: 1.5, backgroundColor: '#f3f4f6', borderTop: '1px solid #e5e7eb' }}
              >
                <Typography variant="body2" color="text.secondary">
                  Viewing chat transcript. Use the note icon above to generate draft notes with AI.
                </Typography>
              </Box>
            )}
          </Box>
        </Paper>
      </Slide>
    </>
  );
};

export default FidesChatPanel;
